/*
 * Social exports — the calls behind the export dialog.
 *
 *   GET  /videos/{id}/export-presets            presets for this video (404 = feature off)
 *   POST /videos/{id}/exports                   create (Idempotency-Key), 202 new / 200 reused
 *   GET  /videos/{id}/exports                   this video's exports, newest first
 *   GET  /videos/{id}/exports/{eid}             one export (polled while active)
 *   POST /videos/{id}/exports/{eid}/cancel
 *   GET  /videos/{id}/exports/{eid}/download    { url, filename }
 */
import { ApiError, getJson, postJson } from '@/shared/api/apiClient';
import type {
  ExportDownload,
  ExportList,
  ExportPresetList,
  ExportRecord,
} from '@/features/exports/types';

const base = (videoId: string) => `/api/v1/videos/${encodeURIComponent(videoId)}`;
const one = (videoId: string, exportId: string) =>
  `${base(videoId)}/exports/${encodeURIComponent(exportId)}`;

export const fetchExportPresets = (videoId: string) =>
  getJson<ExportPresetList>(`${base(videoId)}/export-presets`);

export const fetchExports = (videoId: string) => getJson<ExportList>(`${base(videoId)}/exports`);

export const fetchExport = (videoId: string, exportId: string) =>
  getJson<ExportRecord>(one(videoId, exportId));

/**
 * Creates an export. The Idempotency-Key is the whole retry story: the engine answers a
 * repeat of the same key with the original export instead of making another. The caller
 * therefore reuses one key for retries of the same attempt and makes a new one for each
 * deliberate new attempt (see useVideoExports).
 */
export const createExport = (videoId: string, preset: string, idempotencyKey: string) =>
  postJson<ExportRecord>(
    `${base(videoId)}/exports`,
    { preset },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );

export const cancelExport = (videoId: string, exportId: string) =>
  postJson<ExportRecord>(`${one(videoId, exportId)}/cancel`);

export const fetchExportDownload = (videoId: string, exportId: string) =>
  getJson<ExportDownload>(`${one(videoId, exportId)}/download`);

/** One key per attempt. `crypto.randomUUID` needs a secure context; fall back for http dev. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `exp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/* ---- errors, in plain words ------------------------------------------------------ */

export function errorCodeOf(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || !err.detail || typeof err.detail !== 'object') return undefined;
  const code = (err.detail as { error?: unknown }).error;
  return typeof code === 'string' ? code : undefined;
}

const messageOf = (err: unknown): string | undefined => {
  if (!(err instanceof ApiError) || !err.detail || typeof err.detail !== 'object') return undefined;
  const m = (err.detail as { message?: unknown }).message;
  return typeof m === 'string' && m.trim() ? m.trim() : undefined;
};

/** Codes whose API message is the explanation (it names the limit and the video's length). */
const VERBATIM_CODES = new Set(['duration_exceeds_limit', 'duration_below_minimum', 'too_long_for_size_limit']);

/* A Map, for the reason apiClient's own table is one: `constructor` must not resolve. */
const EXPORT_ERROR_MESSAGES = new Map<string, string>(
  Object.entries({
    export_in_progress: 'You already have an export running. Wait for it to finish, or cancel it.',
    idempotency_key_conflict: 'That request got mixed up. Please try creating the export again.',
    invalid_idempotency_key: 'That request got mixed up. Please try creating the export again.',
    video_not_ready: "This video isn't finished yet, so it can't be exported.",
    unknown_preset: "That format isn't available any more. Pick another one.",
    exports_unavailable: "Exports aren't available right now. Please try again later.",
    storage_unavailable: "We couldn't reach storage. Please try again in a moment.",
    export_expired: 'This export has expired. Create it again to download it.',
    export_not_ready: "This export isn't ready yet.",
    export_not_cancellable: "This export has already finished, so it can't be cancelled.",
    // What the converter reports when an export fails (shown if the API sent no message).
    output_too_large: 'The converted video came out larger than this platform allows.',
    source_unavailable: "We couldn't find the original video file.",
    source_unreadable: "We couldn't read the original video file.",
    output_validation_failed: "The converted file didn't pass our checks, so we didn't keep it.",
    transcode_failed: "We couldn't convert this video.",
    max_attempts_exceeded: "We tried several times and couldn't convert this video.",
    video_unavailable: 'This video is no longer available.',
  }),
);

export const GENERIC_EXPORT_ERROR = 'Something went wrong with the export. Please try again.';

/** Human wording for how long to wait. */
export function describeWait(seconds: number): string {
  if (seconds < 90) return `${Math.max(1, Math.round(seconds))} seconds`;
  return `${Math.round(seconds / 60)} minutes`;
}

/** Plain-language text for a failed export call. Never a bare error code. */
export function describeExportError(err: unknown): string {
  if (!(err instanceof ApiError)) return GENERIC_EXPORT_ERROR;
  const code = errorCodeOf(err);
  if (code && VERBATIM_CODES.has(code)) {
    const m = messageOf(err);
    if (m) return m;
  }
  if (err.status === 429) {
    return err.retryAfterSeconds
      ? `You've created a lot of exports recently. Please try again in about ${describeWait(err.retryAfterSeconds)}.`
      : "You've created a lot of exports recently. Please wait a little and try again.";
  }
  if (code) {
    const known = EXPORT_ERROR_MESSAGES.get(code);
    if (known) return known;
  }
  // ApiError.message is already humanised by apiClient (network copy or generic copy).
  return err.message || GENERIC_EXPORT_ERROR;
}

/** Text for a failed export row: the API's message, else our wording for its code. */
export function describeExportFailure(error: { code: string; message: string } | null | undefined): string {
  if (!error) return GENERIC_EXPORT_ERROR;
  return error.message?.trim() || EXPORT_ERROR_MESSAGES.get(error.code) || GENERIC_EXPORT_ERROR;
}
